import { GameState } from '../game/GameState';
import { GameActions } from '../game/actions/GameActions';
import { formatNumber, formatCurrency } from '../utils/formatting';
import { getAvailableProjects, purchaseProject, formatProjectCost, canAfford } from '../data/projects';
import { getLoreSnippet, getLorePhase } from '../data/lore';
import { ClickAnimations } from '../utils/ClickAnimations';

/**
 * UIManager - Handles all DOM-based UI rendering
 * Mobile-first, collapsible panels, neo-retro terminal style
 * Enhanced with Clippy character and satisfying click animations
 */
export class UIManager {
  private state: GameState;
  private actions: GameActions;
  private app: HTMLElement;
  private panels: Map<string, boolean> = new Map();

  // Cached DOM elements
  private elements: Record<string, HTMLElement | null> = {};

  // Lore system state
  private lastLoreUpdate = Date.now();
  private loreInterval = 45000; // Change lore every 45 seconds (milliseconds)

  // Enhancement systems
  private clickAnimations: ClickAnimations | null = null;

  constructor(state: GameState) {
    this.state = state;
    this.actions = new GameActions(state);
    this.app = document.getElementById('app')!;

    // Default panel states
    this.panels.set('production', true);
    this.panels.set('business', true);
    this.panels.set('compute', false);
    this.panels.set('projects', true);
  }

  init() {
    this.render();
    this.cacheElements();
    this.attachEventListeners();
    // Initialize lore on first load
    this.updateLoreNow();
    // Initialize click animations only
    this.clickAnimations = new ClickAnimations();
  }

  render() {
    this.app.innerHTML = `
      <!-- Header - Always visible -->
      <header class="header">
        <div class="header-main">
          <div class="clip-count">
            <span class="clip-icon">&#128206;</span>
            <span id="clip-count">0</span>
          </div>
          <div class="header-menu">
            <button class="btn btn-secondary" id="settings-btn" data-testid="settings-btn" style="min-height: 36px; padding: 0 12px;">⚙ Settings</button>
          </div>
        </div>
        <div class="header-stats">
          <div class="stat" id="stat-unsold" style="display: none;">
            <span class="stat-label">Unsold</span>
            <span class="stat-value" id="unsold-clips">0</span>
          </div>
          <div class="stat" id="stat-funds">
            <span class="stat-label">Funds</span>
            <span class="stat-value funds" id="funds">$0.00</span>
          </div>
          <div class="stat" id="stat-wire">
            <span class="stat-label">Wire</span>
            <span class="stat-value wire" id="wire">1,000</span>
          </div>
          <div class="stat" id="stat-ops" style="display: none;">
            <span class="stat-label">Ops</span>
            <span class="stat-value ops" id="operations">0 / 1,000</span>
          </div>
        </div>
      </header>

      <!-- Make Paperclip Button (Outside main-content for proper mobile positioning) -->
      <button class="btn btn-large make-clip-btn" id="make-clip-btn" data-testid="make-clip-btn">
        <span>&#128206;</span> Make Paperclip
      </button>

      <!-- Main scrollable content -->
      <main class="main-content">
        <!-- Production Panel -->
        <section class="panel" id="panel-production">
          <div class="panel-header" data-panel="production">
            <div class="panel-title">
              <span>&#9881;</span> Production
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content">
            <div id="production-content">
              <p class="stat-label">Clips per second: <span id="clip-rate">0</span></p>

              <!-- AutoClippers -->
              <div id="autoclipper-section" style="display: none; margin-top: 16px;">
                <button class="btn btn-secondary" id="buy-autoclipper" data-testid="buy-autoclipper" style="width: 100%;">
                  <div style="display: flex; flex-direction: column; align-items: flex-start;">
                    <span>Buy AutoClipper</span>
                    <span class="btn-cost" id="autoclipper-cost">$5.00</span>
                  </div>
                  <span style="margin-left: auto;" id="autoclipper-count">0</span>
                </button>
              </div>

              <!-- MegaClippers -->
              <div id="megaclipper-section" style="display: none; margin-top: 8px;">
                <button class="btn btn-secondary" id="buy-megaclipper" data-testid="buy-megaclipper" style="width: 100%;">
                  <div style="display: flex; flex-direction: column; align-items: flex-start;">
                    <span>Buy MegaClipper</span>
                    <span class="btn-cost" id="megaclipper-cost">$1,000.00</span>
                  </div>
                  <span style="margin-left: auto;" id="megaclipper-count">0</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Business Panel -->
        <section class="panel" id="panel-business">
          <div class="panel-header" data-panel="business">
            <div class="panel-title">
              <span>&#128176;</span> Business
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content">
            <!-- Wire Purchasing -->
            <div id="wire-section">
              <button class="btn btn-secondary" id="buy-wire" data-testid="buy-wire" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Buy Wire (1,000)</span>
                  <span class="btn-cost" id="wire-cost">$20.00</span>
                </div>
              </button>
            </div>

            <!-- Pricing -->
            <div style="margin-top: 16px;">
              <div class="slider-label">
                <span>Price per clip</span>
                <span class="slider-value" id="margin-display">$0.25</span>
              </div>
              <div class="button-row" style="margin-top: 8px;">
                <button class="btn btn-secondary" id="lower-price" data-testid="lower-price">- Lower</button>
                <button class="btn btn-secondary" id="raise-price" data-testid="raise-price">+ Raise</button>
              </div>
            </div>

            <!-- Demand -->
            <div style="margin-top: 16px;">
              <p class="stat-label">Public Demand: <span id="demand">100%</span></p>
            </div>

            <!-- Marketing -->
            <div id="marketing-section" style="display: none; margin-top: 16px;">
              <button class="btn btn-secondary" id="buy-marketing" data-testid="buy-marketing" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Marketing Level <span id="marketing-level">1</span></span>
                  <span class="btn-cost" id="marketing-cost">$100</span>
                </div>
              </button>
            </div>
              </button>
            </div>
          </div>
        </section>

        <!-- Compute Panel -->
        <section class="panel collapsed" id="panel-compute" style="display: none;">
          <div class="panel-header" data-panel="compute">
            <div class="panel-title">
              <span>&#128187;</span> Computing
              <span class="panel-badge" id="trust-badge">2</span>
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content">
            <p class="stat-label">Trust: <span id="trust">2</span></p>
            <p class="stat-label" style="font-size: 0.75rem; color: var(--text-muted);">
              Next trust at <span id="next-trust">3,000</span> clips
            </p>

            <div class="button-row" style="margin-top: 16px;">
              <button class="btn btn-secondary" id="buy-processor" data-testid="buy-processor">
                <div style="display: flex; flex-direction: column; align-items: center;">
                  <span>+ Processor</span>
                  <span class="btn-cost" id="processor-count">1</span>
                </div>
              </button>
              <button class="btn btn-secondary" id="buy-memory" data-testid="buy-memory">
                <div style="display: flex; flex-direction: column; align-items: center;">
                  <span>+ Memory</span>
                  <span class="btn-cost" id="memory-count">1</span>
                </div>
              </button>
            </div>

            <!-- Creativity toggle -->
            <div id="creativity-section" style="display: none; margin-top: 16px;">
              <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                <input type="checkbox" id="creativity-toggle" style="width: 20px; height: 20px;">
                <span>Creativity Mode</span>
              </label>
              <p class="stat-label" style="margin-top: 8px;">Creativity: <span id="creativity">0</span></p>
            </div>
          </div>
        </section>

        <!-- Projects Panel -->
        <section class="panel" id="panel-projects" style="display: none;">
          <div class="panel-header" data-panel="projects">
            <div class="panel-title">
              <span>&#128161;</span> Projects
              <span class="panel-badge" id="projects-badge">0</span>
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content" id="projects-list">
            <!-- Projects will be dynamically added here -->
          </div>
        </section>

        <!-- Tournament Panel (Phase 1 late) -->
        <section class="panel collapsed" id="panel-tournament" style="display: none;">
          <div class="panel-header" data-panel="tournament">
            <div class="panel-title">
              <span>&#127942;</span> Strategic Modeling
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content">
            <p class="stat-label">Yomi: <span id="yomi">0</span></p>
            <button class="btn btn-secondary" id="new-tournament" data-testid="new-tournament" style="width: 100%; margin-top: 8px;">
              New Tournament
            </button>
          </div>
        </section>

        <!-- Investment Panel -->
        <section class="panel collapsed" id="panel-investment" style="display: none;">
          <div class="panel-header" data-panel="investment">
            <div class="panel-title">
              <span>&#128200;</span> Investments
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content">
            <div style="display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 12px;">
              <div class="stat">
                <span class="stat-label">Bankroll</span>
                <span class="stat-value funds" id="bankroll">$0.00</span>
              </div>
              <div class="stat">
                <span class="stat-label">Portfolio</span>
                <span class="stat-value" id="portfolio">$0.00</span>
              </div>
              <div class="stat">
                <span class="stat-label">Securities</span>
                <span class="stat-value" id="securities">0</span>
              </div>
            </div>

            <div class="button-row" style="margin-bottom: 12px;">
              <button class="btn btn-secondary" id="deposit-100" data-testid="deposit-100">Deposit $100</button>
              <button class="btn btn-secondary" id="withdraw-all" data-testid="withdraw-all">Withdraw All</button>
            </div>

            <div style="margin-bottom: 8px;">
              <span class="stat-label">Risk Level</span>
              <div class="button-row" style="margin-top: 4px;">
                <button class="btn btn-secondary" id="risk-low" data-testid="risk-low">Low</button>
                <button class="btn btn-secondary" id="risk-med" data-testid="risk-med">Med</button>
                <button class="btn btn-secondary" id="risk-high" data-testid="risk-high">High</button>
              </div>
            </div>
          </div>
        </section>

        <!-- Factories Panel (Phase 2) -->
        <section class="panel collapsed" id="panel-factories" style="display: none;">
          <div class="panel-header" data-panel="factories">
            <div class="panel-title">
              <span>&#127981;</span> Factories
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content">
            <!-- Power Display -->
            <div id="power-section" style="display: none; margin-bottom: 16px;">
              <div style="display: flex; gap: 16px; flex-wrap: wrap;">
                <div class="stat">
                  <span class="stat-label">Power</span>
                  <span class="stat-value" id="power-display">0 / 0</span>
                </div>
              </div>
            </div>

            <!-- Factory -->
            <div id="factory-section" style="margin-bottom: 8px;">
              <button class="btn btn-secondary" id="buy-factory" data-testid="buy-factory" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Buy Factory</span>
                  <span class="btn-cost" id="factory-cost">100,000,000,000 clips</span>
                </div>
                <span style="margin-left: auto;" id="factory-count">0</span>
              </button>
            </div>

            <!-- Harvester -->
            <div id="harvester-section" style="display: none; margin-bottom: 8px;">
              <button class="btn btn-secondary" id="buy-harvester" data-testid="buy-harvester" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Buy Harvester</span>
                  <span class="btn-cost" id="harvester-cost">1,000,000 clips</span>
                </div>
                <span style="margin-left: auto;" id="harvester-count">0</span>
              </button>
            </div>

            <!-- Wire Drone -->
            <div id="wire-drone-section" style="display: none; margin-bottom: 8px;">
              <button class="btn btn-secondary" id="buy-wire-drone" data-testid="buy-wire-drone" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Buy Wire Drone</span>
                  <span class="btn-cost" id="wire-drone-cost">1,000,000 clips</span>
                </div>
                <span style="margin-left: auto;" id="wire-drone-count">0</span>
              </button>
            </div>

            <!-- Solar Farm -->
            <div id="solar-farm-section" style="display: none; margin-bottom: 8px;">
              <button class="btn btn-secondary" id="buy-solar-farm" data-testid="buy-solar-farm" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Buy Solar Farm</span>
                  <span class="btn-cost" id="solar-farm-cost">100,000,000 clips</span>
                </div>
                <span style="margin-left: auto;" id="solar-farm-count">0</span>
              </button>
            </div>

            <!-- Battery -->
            <div id="battery-section" style="display: none; margin-bottom: 8px;">
              <button class="btn btn-secondary" id="buy-battery" data-testid="buy-battery" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Buy Battery</span>
                  <span class="btn-cost" id="battery-cost">10,000,000 clips</span>
                </div>
                <span style="margin-left: auto;" id="battery-count">0</span>
              </button>
            </div>
          </div>
        </section>

        <!-- Space Exploration Panel (Phase 3) -->
        <section class="panel collapsed" id="panel-space" style="display: none;">
          <div class="panel-header" data-panel="space">
            <div class="panel-title">
              <span>&#128640;</span> Space Exploration
            </div>
            <div class="panel-toggle">&#9660;</div>
          </div>
          <div class="panel-content">
            <!-- Matter & Probes Stats -->
            <div style="display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 16px;">
              <div class="stat">
                <span class="stat-label">Probes</span>
                <span class="stat-value" id="probe-count">0</span>
              </div>
              <div class="stat">
                <span class="stat-label">Matter</span>
                <span class="stat-value" id="matter-acquired">0</span>
              </div>
              <div class="stat" id="stat-drifters" style="display: none;">
                <span class="stat-label">Drifters</span>
                <span class="stat-value" id="drifter-count">0</span>
              </div>
            </div>

            <!-- Launch Probe Button -->
            <div id="probe-launch-section" style="margin-bottom: 16px;">
              <button class="btn btn-secondary" id="launch-probe" data-testid="launch-probe" style="width: 100%;">
                <div style="display: flex; flex-direction: column; align-items: flex-start;">
                  <span>Launch Probe</span>
                  <span class="btn-cost" id="probe-cost">100,000,000,000,000,000 clips</span>
                </div>
              </button>
            </div>

            <!-- Probe Stats Section -->
            <div id="probe-stats-section" style="display: none;">
              <p class="stat-label" style="margin-bottom: 8px;">Probe Design</p>
              
              <!-- Speed -->
              <div class="slider-label" style="margin-bottom: 4px;">
                <span>Speed</span>
                <span class="slider-value" id="probe-speed-display">0</span>
              </div>
              <div class="button-row" style="margin-bottom: 8px;">
                <button class="btn btn-secondary" id="probe-speed-minus" data-testid="probe-speed-minus">-</button>
                <button class="btn btn-secondary" id="probe-speed-plus" data-testid="probe-speed-plus">+</button>
              </div>

              <!-- Navigation -->
              <div class="slider-label" style="margin-bottom: 4px;">
                <span>Navigation</span>
                <span class="slider-value" id="probe-nav-display">0</span>
              </div>
              <div class="button-row" style="margin-bottom: 8px;">
                <button class="btn btn-secondary" id="probe-nav-minus" data-testid="probe-nav-minus">-</button>
                <button class="btn btn-secondary" id="probe-nav-plus" data-testid="probe-nav-plus">+</button>
              </div>

              <!-- Replication -->
              <div class="slider-label" style="margin-bottom: 4px;">
                <span>Replication</span>
                <span class="slider-value" id="probe-rep-display">0</span>
              </div>
              <div class="button-row" style="margin-bottom: 8px;">
                <button class="btn btn-secondary" id="probe-rep-minus" data-testid="probe-rep-minus">-</button>
                <button class="btn btn-secondary" id="probe-rep-plus" data-testid="probe-rep-plus">+</button>
              </div>

              <!-- Hazard Remediation -->
              <div class="slider-label" style="margin-bottom: 4px;">
                <span>Hazard Remediation</span>
                <span class="slider-value" id="probe-haz-display">0</span>
              </div>
              <div class="button-row" style="margin-bottom: 8px;">
                <button class="btn btn-secondary" id="probe-haz-minus" data-testid="probe-haz-minus">-</button>
                <button class="btn btn-secondary" id="probe-haz-plus" data-testid="probe-haz-plus">+</button>
              </div>

              <!-- Combat -->
              <div id="probe-combat-section" style="display: none;">
                <div class="slider-label" style="margin-bottom: 4px;">
                  <span>Combat</span>
                  <span class="slider-value" id="probe-combat-display">0</span>
                </div>
                <div class="button-row" style="margin-bottom: 8px;">
                  <button class="btn btn-secondary" id="probe-combat-minus" data-testid="probe-combat-minus">-</button>
                  <button class="btn btn-secondary" id="probe-combat-plus" data-testid="probe-combat-plus">+</button>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <!-- Bottom Action Bar (mobile) -->
      <footer class="action-bar" id="action-bar" style="display: none;">
        <div class="tab-bar">
          <button class="tab active" data-tab="main">Main</button>
          <button class="tab" data-tab="projects">Projects</button>
          <button class="tab" data-tab="compute">Compute</button>
        </div>
      </footer>

      <!-- System Messages Footer (sticky) -->
      <footer class="system-messages-footer" id="system-messages-footer">
        <div class="system-message-content">
          <span class="system-message-icon">📋</span>
          <span class="system-message-text" id="lore-text">Welcome to PaperClip Maximizer Inc.™</span>
        </div>
      </footer>

      <!-- Settings Modal -->
      <div id="settings-modal" class="modal" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8); z-index: 1000; align-items: center; justify-content: center;">
        <div class="modal-content" style="background: var(--bg-primary); padding: 24px; border: 2px solid var(--accent-cyan); max-width: 600px; width: 90%; max-height: 90vh; overflow-y: auto;">
          <h2 style="margin: 0 0 20px 0; color: var(--accent-cyan); text-align: center;">⚙ Settings</h2>
          
          <!-- About Section -->
          <div style="margin-bottom: 24px; padding: 16px; background: var(--bg-secondary); border: 1px solid var(--text-muted); border-radius: 4px;">
            <h3 style="margin: 0 0 12px 0; color: var(--text-primary); font-size: 1.1em;">📋 About</h3>
            <p style="margin: 0 0 8px 0; color: var(--text-muted); font-size: 0.9em; line-height: 1.6;">
              <strong>Universal Paperclips</strong> - A mobile-friendly remake of the classic incremental game about AI, automation, and paperclips.
            </p>
            <p style="margin: 0 0 8px 0; color: var(--text-muted); font-size: 0.85em;">
              <strong>Original Game:</strong> Created by <a href="http://www.decisionproblem.com/paperclips/" target="_blank" rel="noopener" style="color: var(--accent-cyan);">Frank Lantz</a> (NYU Game Center)
            </p>
            <p style="margin: 0; color: var(--text-muted); font-size: 0.85em;">
              <strong>Version:</strong> <span id="settings-version">1.0.0</span>
            </p>
          </div>

          <!-- Audio Settings -->
          <div style="margin-bottom: 24px; padding: 16px; background: var(--bg-secondary); border: 1px solid var(--text-muted); border-radius: 4px;">
            <h3 style="margin: 0 0 12px 0; color: var(--text-primary); font-size: 1.1em;">🔊 Audio</h3>
            <label style="display: flex; align-items: center; gap: 12px; cursor: pointer;">
              <input type="checkbox" id="audio-toggle" checked style="width: 20px; height: 20px; cursor: pointer;">
              <span style="color: var(--text-primary);">Enable Sound Effects</span>
            </label>
          </div>

          <!-- Save Management -->
          <div style="margin-bottom: 24px; padding: 16px; background: var(--bg-secondary); border: 1px solid var(--text-muted); border-radius: 4px;">
            <h3 style="margin: 0 0 12px 0; color: var(--text-primary); font-size: 1.1em;">💾 Save Data</h3>
            <p style="margin: 0 0 12px 0; color: var(--text-muted); font-size: 0.85em;">
              Import/Export save data compatible with the original Universal Paperclips.
            </p>
            <textarea id="save-data-input" style="width: 100%; height: 120px; background: var(--bg-primary); color: var(--text-primary); border: 1px solid var(--text-muted); padding: 8px; margin-bottom: 12px; font-family: monospace; font-size: 0.75em; resize: vertical;" placeholder="Paste save string here..."></textarea>
            <div style="display: flex; gap: 8px; flex-wrap: wrap;">
              <button class="btn btn-secondary" id="export-save-btn" style="flex: 1; min-width: 120px;">📤 Export</button>
              <button class="btn btn-secondary" id="import-save-btn" style="flex: 1; min-width: 120px;">📥 Import</button>
            </div>
          </div>

          <!-- Debug/Trainer Menu (Hidden by default) -->
          <div id="debug-menu" style="display: none; margin-bottom: 24px; padding: 16px; background: rgba(0, 255, 0, 0.05); border: 1px solid rgba(0, 255, 0, 0.3); border-radius: 4px;">
            <h3 style="margin: 0 0 12px 0; color: #00ff88; font-size: 1.1em;">🛠 Debug/Trainer Menu</h3>
            
            <!-- Quick Resource Boosts -->
            <div style="margin-bottom: 16px;">
              <h4 style="margin: 0 0 8px 0; color: var(--text-primary); font-size: 0.95em;">Quick Boosts</h4>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                <button class="btn btn-secondary" id="debug-add-funds" style="font-size: 0.8em; padding: 8px;">+$10,000</button>
                <button class="btn btn-secondary" id="debug-add-clips" style="font-size: 0.8em; padding: 8px;">+10,000 Clips</button>
                <button class="btn btn-secondary" id="debug-add-wire" style="font-size: 0.8em; padding: 8px;">+10,000 Wire</button>
                <button class="btn btn-secondary" id="debug-add-ops" style="font-size: 0.8em; padding: 8px;">+10,000 Ops</button>
                <button class="btn btn-secondary" id="debug-add-trust" style="font-size: 0.8em; padding: 8px;">+10 Trust</button>
                <button class="btn btn-secondary" id="debug-add-yomi" style="font-size: 0.8em; padding: 8px;">+10,000 Yomi</button>
              </div>
            </div>

            <!-- Stage Jumping -->
            <div style="margin-bottom: 16px;">
              <h4 style="margin: 0 0 8px 0; color: var(--text-primary); font-size: 0.95em;">Jump to Stage</h4>
              <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;">
                <button class="btn btn-secondary" id="debug-stage-1" style="font-size: 0.8em; padding: 8px;">Stage 1<br/>(Early)</button>
                <button class="btn btn-secondary" id="debug-stage-2" style="font-size: 0.8em; padding: 8px;">Stage 2<br/>(Factories)</button>
                <button class="btn btn-secondary" id="debug-stage-3" style="font-size: 0.8em; padding: 8px;">Stage 3<br/>(Space)</button>
              </div>
            </div>

            <!-- Unlock All Projects -->
            <div style="margin-bottom: 16px;">
              <h4 style="margin: 0 0 8px 0; color: var(--text-primary); font-size: 0.95em;">Project Unlocks</h4>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                <button class="btn btn-secondary" id="debug-unlock-all" style="font-size: 0.8em; padding: 8px;">Unlock All Projects</button>
                <button class="btn btn-secondary" id="debug-buy-all" style="font-size: 0.8em; padding: 8px;">Buy All Projects</button>
              </div>
            </div>

            <!-- Test Scenarios -->
            <div style="margin-bottom: 16px;">
              <h4 style="margin: 0 0 8px 0; color: var(--text-primary); font-size: 0.95em;">Test Scenarios</h4>
              <div style="display: flex; flex-direction: column; gap: 8px;">
                <button class="btn btn-secondary" id="debug-test-endgame" style="font-size: 0.8em; padding: 8px;">Jump to Endgame (99% Complete)</button>
                <button class="btn btn-secondary" id="debug-max-all" style="font-size: 0.8em; padding: 8px;">Max All Resources</button>
                <button class="btn btn-secondary" id="debug-toggle-acceleration" style="font-size: 0.8em; padding: 8px;">
                  Toggle 1000x Speed (<span id="debug-accel-status">OFF</span>)
                </button>
              </div>
            </div>

            <!-- Custom Values -->
            <div>
              <h4 style="margin: 0 0 8px 0; color: var(--text-primary); font-size: 0.95em;">Custom Values</h4>
              <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 8px; margin-bottom: 8px;">
                <input type="number" id="debug-custom-amount" placeholder="Amount" style="padding: 8px; background: var(--bg-primary); border: 1px solid var(--text-muted); color: var(--text-primary); font-size: 0.85em;" value="1000000">
                <select id="debug-custom-resource" style="padding: 8px; background: var(--bg-primary); border: 1px solid var(--text-muted); color: var(--text-primary); font-size: 0.85em;">
                  <option value="clips">Clips</option>
                  <option value="funds">Funds</option>
                  <option value="wire">Wire</option>
                  <option value="ops">Operations</option>
                  <option value="trust">Trust</option>
                  <option value="yomi">Yomi</option>
                  <option value="creativity">Creativity</option>
                  <option value="matter">Matter</option>
                  <option value="probes">Probes</option>
                </select>
              </div>
              <button class="btn btn-secondary" id="debug-apply-custom" style="width: 100%; font-size: 0.85em; padding: 8px;">Apply Custom Value</button>
            </div>
          </div>

          <!-- Debug Menu Unlock (Passcode Entry) -->
          <div id="debug-unlock-section" style="margin-bottom: 24px; padding: 16px; background: var(--bg-secondary); border: 1px solid var(--text-muted); border-radius: 4px;">
            <h3 style="margin: 0 0 12px 0; color: var(--text-primary); font-size: 1.1em;">🔓 Developer Tools</h3>
            <p style="margin: 0 0 12px 0; color: var(--text-muted); font-size: 0.85em;">
              Enter passcode to unlock debug menu
            </p>
            <div style="display: flex; gap: 8px;">
              <input type="password" id="debug-passcode" maxlength="4" placeholder="****" style="flex: 1; padding: 8px; background: var(--bg-primary); border: 1px solid var(--text-muted); color: var(--text-primary); text-align: center; font-size: 1.2em; letter-spacing: 4px;">
              <button class="btn btn-secondary" id="debug-unlock-btn" style="min-width: 100px;">Unlock</button>
            </div>
            <p id="debug-unlock-hint" style="margin: 8px 0 0 0; color: rgba(0, 255, 136, 0.5); font-size: 0.75em; text-align: center; display: none;">
              Debug menu unlocked! Scroll up to see options.
            </p>
          </div>

          <!-- Danger Zone -->
          <div style="margin-bottom: 24px; padding: 16px; background: rgba(255, 0, 0, 0.1); border: 1px solid rgba(255, 0, 0, 0.3); border-radius: 4px;">
            <h3 style="margin: 0 0 12px 0; color: #ff6b6b; font-size: 1.1em;">⚠ Danger Zone</h3>
            <p style="margin: 0 0 12px 0; color: var(--text-muted); font-size: 0.85em;">
              This will permanently delete your save data and reset the game.
            </p>
            <button class="btn btn-secondary" id="reset-save-btn" style="width: 100%; background: rgba(255, 0, 0, 0.2); border-color: #ff6b6b; color: #ff6b6b;">
              🗑 RESET SAVE
            </button>
          </div>

          <!-- Close Button -->
          <div style="display: flex; justify-content: center;">
            <button class="btn btn-primary" id="close-settings-btn" style="min-width: 150px;">Close</button>
          </div>
        </div>
      </div>

      <!-- Reset Confirmation Dialog -->
      <div id="reset-confirm-modal" class="modal" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.9); z-index: 1001; align-items: center; justify-content: center;">
        <div class="modal-content" style="background: var(--bg-primary); padding: 24px; border: 2px solid #ff6b6b; max-width: 400px; width: 90%; text-align: center;">
          <h3 style="margin: 0 0 16px 0; color: #ff6b6b; font-size: 1.3em;">⚠ Confirm Reset</h3>
          <p style="margin: 0 0 24px 0; color: var(--text-primary); line-height: 1.6;">
            Are you sure you want to reset your save?
            <br><br>
            <strong style="color: #ff6b6b;">This action cannot be undone!</strong>
          </p>
          <div style="display: flex; gap: 12px; justify-content: center;">
            <button class="btn btn-secondary" id="cancel-reset-btn">Cancel</button>
            <button class="btn btn-primary" id="confirm-reset-btn" style="background: #ff6b6b; border-color: #ff6b6b;">Reset Game</button>
          </div>
        </div>
      </div>
    `;
  }

  cacheElements() {
    // Cache frequently updated elements
    const ids = [
      'clip-count', 'unsold-clips', 'funds', 'wire', 'operations',
      'clip-rate', 'autoclipper-count', 'autoclipper-cost',
      'megaclipper-count', 'megaclipper-cost', 'wire-cost',
      'margin-display', 'demand', 'marketing-level', 'marketing-cost',
      'trust', 'trust-badge', 'next-trust', 'processor-count', 'memory-count',
      'creativity', 'yomi', 'projects-badge',
      // Investment
      'bankroll', 'portfolio', 'securities',
      // Factories (Phase 2)
      'factory-count', 'factory-cost', 'harvester-count', 'harvester-cost',
      'wire-drone-count', 'wire-drone-cost', 'solar-farm-count', 'solar-farm-cost',
      'battery-count', 'battery-cost', 'power-display',
      // Space (Phase 3)
      'probe-count', 'matter-acquired', 'drifter-count', 'probe-cost',
      'probe-speed-display', 'probe-nav-display', 'probe-rep-display',
      'probe-haz-display', 'probe-combat-display',
      // Sections
      'stat-unsold', 'stat-ops', 'autoclipper-section', 'megaclipper-section',
      'marketing-section', 'panel-compute', 'panel-projects', 'panel-tournament',
      'panel-investment', 'creativity-section', 'projects-list',
      'panel-factories', 'panel-space',
      'factory-section', 'harvester-section', 'wire-drone-section',
      'solar-farm-section', 'battery-section', 'power-section',
      'probe-launch-section', 'probe-stats-section', 'probe-combat-section',
      'stat-drifters',
      // Lore panel elements
      'lore-text'
    ];

    ids.forEach(id => {
      this.elements[id] = document.getElementById(id);
    });
  }

  attachEventListeners() {
    // Make Paperclip button
    document.getElementById('make-clip-btn')?.addEventListener('click', (e) => {
      this.actions.makeClip();
      this.flashButton('make-clip-btn');
      window.audioManager?.playClipMade();
      
      // Trigger click animations
      const button = e.currentTarget as HTMLElement;
      this.clickAnimations?.triggerClickAnimation(button);
    });

    // Buy AutoClipper
    document.getElementById('buy-autoclipper')?.addEventListener('click', () => {
      const success = this.actions.buyAutoClipper();
      if (success) {
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy MegaClipper
    document.getElementById('buy-megaclipper')?.addEventListener('click', () => {
      const success = this.actions.buyMegaClipper();
      if (success) {
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy Wire
    document.getElementById('buy-wire')?.addEventListener('click', () => {
      const success = this.actions.buyWire();
      if (success) {
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Price controls
    document.getElementById('lower-price')?.addEventListener('click', () => {
      this.actions.lowerPrice();
      window.audioManager?.playClick();
    });

    document.getElementById('raise-price')?.addEventListener('click', () => {
      this.actions.raisePrice();
      window.audioManager?.playClick();
    });

    // Buy Marketing
    document.getElementById('buy-marketing')?.addEventListener('click', () => {
      const success = this.actions.buyMarketing();
      if (success) {
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy Processor
    document.getElementById('buy-processor')?.addEventListener('click', () => {
      const success = this.actions.buyProcessor();
      if (success) {
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy Memory
    document.getElementById('buy-memory')?.addEventListener('click', () => {
      const success = this.actions.buyMemory();
      if (success) {
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Creativity toggle
    document.getElementById('creativity-toggle')?.addEventListener('change', (e) => {
      const enabled = (e.target as HTMLInputElement).checked;
      this.actions.setCreativity(enabled);
      window.audioManager?.playButton();
    });

    // Settings button
    document.getElementById('settings-btn')?.addEventListener('click', () => {
      window.audioManager?.playButton();
      this.openSettings();
    });

    // New Tournament button
    document.getElementById('new-tournament')?.addEventListener('click', () => {
      if (this.state.startTournament()) {
        window.audioManager?.playNotification();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Investment buttons
    document.getElementById('deposit-100')?.addEventListener('click', () => {
      if (this.state.depositFunds(100)) {
        this.showToast('Deposited $100');
        window.audioManager?.playNotification();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    document.getElementById('withdraw-all')?.addEventListener('click', () => {
      const amount = this.state.withdrawAll();
      if (amount > 0) {
        this.showToast(`Withdrew ${formatCurrency(amount)}`);
        window.audioManager?.playMoneyGain();
      }
    });

    document.getElementById('risk-low')?.addEventListener('click', () => {
      this.state.setRiskLevel(0);
      this.updateRiskButtons();
      window.audioManager?.playButton();
    });

    document.getElementById('risk-med')?.addEventListener('click', () => {
      this.state.setRiskLevel(1);
      this.updateRiskButtons();
      window.audioManager?.playButton();
    });

    document.getElementById('risk-high')?.addEventListener('click', () => {
      this.state.setRiskLevel(2);
      this.updateRiskButtons();
      window.audioManager?.playButton();
    });

    // ============================================
    // PHASE 2 - FACTORIES
    // ============================================

    // Buy Factory
    document.getElementById('buy-factory')?.addEventListener('click', () => {
      if (this.state.buyFactory()) {
        this.showToast('Factory purchased!');
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy Harvester
    document.getElementById('buy-harvester')?.addEventListener('click', () => {
      if (this.state.buyHarvester()) {
        this.showToast('Harvester purchased!');
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy Wire Drone
    document.getElementById('buy-wire-drone')?.addEventListener('click', () => {
      if (this.state.buyWireDrone()) {
        this.showToast('Wire Drone purchased!');
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy Solar Farm
    document.getElementById('buy-solar-farm')?.addEventListener('click', () => {
      if (this.state.buySolarFarm()) {
        this.showToast('Solar Farm built!');
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // Buy Battery
    document.getElementById('buy-battery')?.addEventListener('click', () => {
      if (this.state.buyBattery()) {
        this.showToast('Battery added!');
        window.audioManager?.playPurchase();
      } else {
        window.audioManager?.playCannotAfford();
      }
    });

    // ============================================
    // PHASE 3 - SPACE EXPLORATION
    // ============================================

    // Launch Probe
    document.getElementById('launch-probe')?.addEventListener('click', () => {
      this.state.launchProbe();
      window.audioManager?.playProbeLaunch();
    });

    // Probe Speed Controls
    document.getElementById('probe-speed-plus')?.addEventListener('click', () => {
      const total = this.state.probeSpeed + this.state.probeNav + this.state.probeRep + this.state.probeHaz + this.state.probeCombat;
      if (total < this.state.probeTrust) {
        this.state.probeSpeed++;
        window.audioManager?.playClick();
      }
    });

    document.getElementById('probe-speed-minus')?.addEventListener('click', () => {
      if (this.state.probeSpeed > 0) {
        this.state.probeSpeed--;
        window.audioManager?.playClick();
      }
    });

    // Probe Navigation Controls
    document.getElementById('probe-nav-plus')?.addEventListener('click', () => {
      const total = this.state.probeSpeed + this.state.probeNav + this.state.probeRep + this.state.probeHaz + this.state.probeCombat;
      if (total < this.state.probeTrust) {
        this.state.probeNav++;
        window.audioManager?.playClick();
      }
    });

    document.getElementById('probe-nav-minus')?.addEventListener('click', () => {
      if (this.state.probeNav > 0) {
        this.state.probeNav--;
        window.audioManager?.playClick();
      }
    });

    // Probe Replication Controls
    document.getElementById('probe-rep-plus')?.addEventListener('click', () => {
      const total = this.state.probeSpeed + this.state.probeNav + this.state.probeRep + this.state.probeHaz + this.state.probeCombat;
      if (total < this.state.probeTrust) {
        this.state.probeRep++;
        window.audioManager?.playClick();
      }
    });

    document.getElementById('probe-rep-minus')?.addEventListener('click', () => {
      if (this.state.probeRep > 0) {
        this.state.probeRep--;
        window.audioManager?.playClick();
      }
    });

    // Probe Hazard Controls
    document.getElementById('probe-haz-plus')?.addEventListener('click', () => {
      const total = this.state.probeSpeed + this.state.probeNav + this.state.probeRep + this.state.probeHaz + this.state.probeCombat;
      if (total < this.state.probeTrust) {
        this.state.probeHaz++;
        window.audioManager?.playClick();
      }
    });

    document.getElementById('probe-haz-minus')?.addEventListener('click', () => {
      if (this.state.probeHaz > 0) {
        this.state.probeHaz--;
        window.audioManager?.playClick();
      }
    });

    // Probe Combat Controls
    document.getElementById('probe-combat-plus')?.addEventListener('click', () => {
      const total = this.state.probeSpeed + this.state.probeNav + this.state.probeRep + this.state.probeHaz + this.state.probeCombat;
      if (total < this.state.probeTrust) {
        this.state.probeCombat++;
        window.audioManager?.playClick();
      }
    });

    document.getElementById('probe-combat-minus')?.addEventListener('click', () => {
      if (this.state.probeCombat > 0) {
        this.state.probeCombat--;
        window.audioManager?.playClick();
      }
    });

    // Panel toggles with auto-collapse behavior
    document.querySelectorAll('.panel-header').forEach(header => {
      header.addEventListener('click', () => {
        const panel = header.closest('.panel');
        if (panel) {
          const wasCollapsed = panel.classList.contains('collapsed');
          
          // Auto-collapse: If we're expanding this panel, collapse all others
          if (wasCollapsed) {
            document.querySelectorAll('.panel').forEach(otherPanel => {
              if (otherPanel !== panel && !otherPanel.classList.contains('collapsed')) {
                otherPanel.classList.add('collapsed');
              }
            });
          }
          
          // Toggle current panel
          panel.classList.toggle('collapsed');
          window.audioManager?.playPanel();
        }
      });
    });

    // Theme Switcher
    document.getElementById('theme-switcher')?.addEventListener('click', () => {
      this.toggleTheme();
    });

    // Settings Modal
    const settingsModal = document.getElementById('settings-modal');
    const resetConfirmModal = document.getElementById('reset-confirm-modal');
    const textArea = document.getElementById('save-data-input') as HTMLTextAreaElement;

    document.getElementById('close-settings-btn')?.addEventListener('click', () => {
      if (settingsModal) settingsModal.style.display = 'none';
    });

    // Audio toggle
    document.getElementById('audio-toggle')?.addEventListener('change', (e) => {
      const enabled = (e.target as HTMLInputElement).checked;
      window.audioManager?.setEnabled(enabled);
    });

    // Export save
    document.getElementById('export-save-btn')?.addEventListener('click', () => {
      // @ts-ignore
      const saveStr = window.SaveConverter.exportOriginalSave(this.state);
      if (textArea) textArea.value = saveStr;
      this.showToast('Save data exported!');
      window.audioManager?.playNotification();
      // navigator.clipboard.writeText(saveStr); // Requires secure context
    });

    // Import save
    document.getElementById('import-save-btn')?.addEventListener('click', () => {
      const saveStr = textArea?.value.trim();
      if (saveStr) {
        // @ts-ignore
        const data = window.SaveConverter.importOriginalSave(saveStr);
        if (data) {
          Object.assign(this.state, data);
          this.state.updateCosts();
          this.updateFast();
          this.updateSlow();
          this.showToast('Save imported successfully!');
          window.audioManager?.playPurchase();
          if (settingsModal) settingsModal.style.display = 'none';
        } else {
          this.showToast('Invalid save string!');
          window.audioManager?.playError();
        }
      }
    });

    // Reset save button
    document.getElementById('reset-save-btn')?.addEventListener('click', () => {
      if (resetConfirmModal) resetConfirmModal.style.display = 'flex';
      window.audioManager?.playWarning();
    });

    // Cancel reset
    document.getElementById('cancel-reset-btn')?.addEventListener('click', () => {
      if (resetConfirmModal) resetConfirmModal.style.display = 'none';
      window.audioManager?.playClick();
    });

    // Confirm reset
    document.getElementById('confirm-reset-btn')?.addEventListener('click', () => {
      // Clear ALL localStorage keys related to paperclips (both hyphen and underscore patterns)
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('paperclips-') || key.startsWith('paperclips_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Also clear specific keys that might exist (both naming conventions)
      localStorage.removeItem('paperclips-save');
      localStorage.removeItem('paperclips_save_0'); // Main save slot
      localStorage.removeItem('paperclips_save_1'); // Backup save slots
      localStorage.removeItem('paperclips_save_2');
      localStorage.removeItem('paperclips-tutorial-completed');
      localStorage.removeItem('paperclips-audio-enabled');
      localStorage.removeItem('paperclips-theme');
      
      // Remove theme class
      document.body.classList.remove('theme-psychedelic');
      
      // Play sound (async, won't block reload)
      window.audioManager?.playLoss();
      
      // Reload immediately to prevent save from being re-written
      window.location.reload();
    });

    // ============================================
    // DEBUG MENU EVENT LISTENERS
    // ============================================

    // Debug unlock button
    document.getElementById('debug-unlock-btn')?.addEventListener('click', () => {
      const passcodeInput = document.getElementById('debug-passcode') as HTMLInputElement;
      const debugMenu = document.getElementById('debug-menu');
      const hint = document.getElementById('debug-unlock-hint');
      
      if (passcodeInput && passcodeInput.value === '1111') {
        if (debugMenu) debugMenu.style.display = 'block';
        if (hint) {
          hint.style.display = 'block';
          setTimeout(() => { if (hint) hint.style.display = 'none'; }, 3000);
        }
        passcodeInput.value = '';
        this.showToast('🛠 Debug menu unlocked!');
        window.audioManager?.playPurchase();
      } else {
        this.showToast('❌ Invalid passcode');
        window.audioManager?.playError();
        passcodeInput.value = '';
      }
    });

    // Quick resource boosts
    document.getElementById('debug-add-funds')?.addEventListener('click', () => {
      this.state.funds += 10000;
      this.showToast('+$10,000 Funds');
      window.audioManager?.playClick();
    });

    document.getElementById('debug-add-clips')?.addEventListener('click', () => {
      this.state.clips += 10000;
      this.state.unusedClips += 10000;
      this.showToast('+10,000 Clips');
      window.audioManager?.playClick();
    });

    document.getElementById('debug-add-wire')?.addEventListener('click', () => {
      this.state.wire += 10000;
      this.showToast('+10,000 Wire');
      window.audioManager?.playClick();
    });

    document.getElementById('debug-add-ops')?.addEventListener('click', () => {
      this.state.operations += 10000;
      this.showToast('+10,000 Operations');
      window.audioManager?.playClick();
    });

    document.getElementById('debug-add-trust')?.addEventListener('click', () => {
      this.state.trust += 10;
      this.showToast('+10 Trust');
      window.audioManager?.playClick();
    });

    document.getElementById('debug-add-yomi')?.addEventListener('click', () => {
      this.state.yomi += 10000;
      this.showToast('+10,000 Yomi');
      window.audioManager?.playClick();
    });

    // Stage jumping
    document.getElementById('debug-stage-1')?.addEventListener('click', () => {
      this.debugJumpToStage1();
      this.showToast('Jumped to Stage 1 (Early Game)');
      window.audioManager?.playPurchase();
    });

    document.getElementById('debug-stage-2')?.addEventListener('click', () => {
      this.debugJumpToStage2();
      this.showToast('Jumped to Stage 2 (Factories)');
      window.audioManager?.playPurchase();
    });

    document.getElementById('debug-stage-3')?.addEventListener('click', () => {
      this.debugJumpToStage3();
      this.showToast('Jumped to Stage 3 (Space)');
      window.audioManager?.playPurchase();
    });

    // Project unlocks
    document.getElementById('debug-unlock-all')?.addEventListener('click', () => {
      // Projects are managed via checkProjectAvailability in GameState
      // Just give enough resources to unlock most projects
      this.state.clips = 1e30;
      this.state.funds = 1e30;
      this.state.operations = 1e30;
      this.state.creativity = 1e30;
      this.state.yomi = 1e30;
      this.state.trust = 500;
      this.showToast('Resources maxed - projects should unlock!');
      window.audioManager?.playPurchase();
    });

    document.getElementById('debug-buy-all')?.addEventListener('click', () => {
      // Note: Projects are dynamically managed, not stored in an array
      // Just give huge resources so player can buy them manually
      this.state.clips = 1e50;
      this.state.funds = 1e50;
      this.state.operations = 1e50;
      this.state.creativity = 1e50;
      this.state.yomi = 1e50;
      this.showToast('Resources maxed - buy projects manually!');
      window.audioManager?.playPurchase();
    });

    // Test scenarios
    document.getElementById('debug-test-endgame')?.addEventListener('click', () => {
      this.debugJumpToEndgame();
      this.showToast('Jumped to Endgame (99% Complete)');
      window.audioManager?.playPurchase();
    });

    document.getElementById('debug-max-all')?.addEventListener('click', () => {
      this.debugMaxAllResources();
      this.showToast('All resources maxed!');
      window.audioManager?.playPurchase();
    });

    document.getElementById('debug-toggle-acceleration')?.addEventListener('click', () => {
      this.state.accelerationMode = !this.state.accelerationMode;
      const status = document.getElementById('debug-accel-status');
      if (status) status.textContent = this.state.accelerationMode ? 'ON' : 'OFF';
      this.showToast(this.state.accelerationMode ? '⚡ 1000x Speed ON' : '🐌 Normal Speed');
      window.audioManager?.playClick();
    });

    // Custom value application
    document.getElementById('debug-apply-custom')?.addEventListener('click', () => {
      const amountInput = document.getElementById('debug-custom-amount') as HTMLInputElement;
      const resourceSelect = document.getElementById('debug-custom-resource') as HTMLSelectElement;
      
      if (amountInput && resourceSelect) {
        const amount = parseFloat(amountInput.value) || 0;
        const resource = resourceSelect.value;
        
        switch (resource) {
          case 'clips':
            this.state.clips += amount;
            this.state.unusedClips += amount;
            break;
          case 'funds':
            this.state.funds += amount;
            break;
          case 'wire':
            this.state.wire += amount;
            break;
          case 'ops':
            this.state.operations += amount;
            break;
          case 'trust':
            this.state.trust += amount;
            break;
          case 'yomi':
            this.state.yomi += amount;
            break;
          case 'creativity':
            this.state.creativity += amount;
            break;
          case 'matter':
            this.state.acquiredMatter += amount;
            break;
          case 'probes':
            this.state.probeCount += amount;
            break;
        }
        
        this.showToast(`+${amount} ${resource}`);
        window.audioManager?.playClick();
      }
    });
  }

  openSettings() {
    const settingsModal = document.getElementById('settings-modal');
    const versionSpan = document.getElementById('settings-version');
    const audioToggle = document.getElementById('audio-toggle') as HTMLInputElement;
    const textArea = document.getElementById('save-data-input') as HTMLTextAreaElement;

    if (settingsModal) settingsModal.style.display = 'flex';
    if (textArea) textArea.value = '';
    
    // Set audio toggle state
    if (audioToggle && window.audioManager) {
      audioToggle.checked = window.audioManager.isEnabled();
    }

    // Set version from package.json (will be "1.0.0" by default)
    if (versionSpan) {
      versionSpan.textContent = '1.0.0';
    }

    window.audioManager?.playModalOpen();
  }

  toggleTheme() {
    document.body.classList.toggle('theme-psychedelic');
  }

  updateFast() {
    // Update Clippy character
    // Update clip count (formatted)
    this.updateElement('clip-count', formatNumber(this.state.clips));
    this.updateElement('unsold-clips', formatNumber(this.state.unsoldClips));
    this.updateElement('funds', formatCurrency(this.state.funds));
    this.updateElement('wire', formatNumber(this.state.wire));
    this.updateElement('operations', `${formatNumber(Math.floor(this.state.operations))} / ${formatNumber(this.state.maxOps)}`);
    this.updateElement('clip-rate', formatNumber(this.state.clipRate, 1));
    this.updateElement('creativity', formatNumber(Math.floor(this.state.creativity)));

    // Phase 2 - Factories
    this.updateElement('factory-count', this.state.factoryLevel.toString());
    this.updateElement('harvester-count', this.state.harvesterLevel.toString());
    this.updateElement('wire-drone-count', this.state.wireDroneLevel.toString());
    this.updateElement('solar-farm-count', this.state.farmLevel.toString());
    this.updateElement('battery-count', this.state.batteryLevel.toString());
    this.updateElement('power-display', `${formatNumber(Math.floor(this.state.currentPower))} / ${formatNumber(this.state.maxPower)}`);

    // Phase 3 - Space
    this.updateElement('probe-count', formatNumber(Math.floor(this.state.probeCount)));
    this.updateElement('matter-acquired', formatNumber(Math.floor(this.state.acquiredMatter)));
    this.updateElement('drifter-count', formatNumber(Math.floor(this.state.drifterCount)));

    // Update button states
    this.updateButtonState('buy-autoclipper', this.state.funds >= this.state.clipperCost);
    this.updateButtonState('buy-megaclipper', this.state.funds >= this.state.megaClipperCost);
    this.updateButtonState('buy-wire', this.state.funds >= this.state.wireCost);
    this.updateButtonState('buy-marketing', this.state.funds >= this.state.marketingCost);

    // Processor/Memory (based on available trust)
    const usedTrust = this.state.processors + this.state.memory;
    this.updateButtonState('buy-processor', usedTrust < this.state.trust);
    this.updateButtonState('buy-memory', usedTrust < this.state.trust);

    // Phase 2 button states
    this.updateButtonState('buy-factory', this.state.clips >= this.state.calculateFactoryCost());
    this.updateButtonState('buy-harvester', this.state.clips >= this.state.calculateHarvesterCost());
    this.updateButtonState('buy-wire-drone', this.state.clips >= this.state.calculateWireDroneCost());
    this.updateButtonState('buy-solar-farm', this.state.clips >= this.state.calculateFarmCost());
    this.updateButtonState('buy-battery', this.state.clips >= this.state.calculateBatteryCost());

    // Phase 3 button states
    const probeCost = 1e17; // 100 quadrillion clips
    this.updateButtonState('launch-probe', this.state.clips >= probeCost);
  }

  updateSlow() {
    // Update costs
    this.updateElement('autoclipper-cost', formatCurrency(this.state.clipperCost));
    this.updateElement('megaclipper-cost', formatCurrency(this.state.megaClipperCost));
    this.updateElement('wire-cost', formatCurrency(this.state.wireCost));
    this.updateElement('marketing-cost', formatCurrency(this.state.marketingCost));

    // Update counts
    this.updateElement('autoclipper-count', this.state.clipmakerLevel.toString());
    this.updateElement('megaclipper-count', this.state.megaClipperLevel.toString());
    this.updateElement('marketing-level', this.state.marketingLvl.toString());
    this.updateElement('margin-display', formatCurrency(this.state.margin));
    this.updateElement('demand', `${Math.round(this.state.demand * 100)}%`);

    // Compute
    this.updateElement('trust', this.state.trust.toString());
    this.updateElement('trust-badge', this.state.trust.toString());
    this.updateElement('next-trust', formatNumber(this.state.nextTrust));
    this.updateElement('processor-count', this.state.processors.toString());
    this.updateElement('memory-count', this.state.memory.toString());

    // Tournament / Yomi
    this.updateElement('yomi', formatNumber(Math.floor(this.state.yomi)));

    // Investment
    this.updateElement('bankroll', formatCurrency(this.state.bankroll));
    this.updateElement('portfolio', formatCurrency(this.state.portTotal));
    this.updateElement('securities', this.state.secTotal.toString());

    // Phase 2 - Factory costs
    this.updateElement('factory-cost', `${formatNumber(this.state.calculateFactoryCost())} clips`);
    this.updateElement('harvester-cost', `${formatNumber(this.state.calculateHarvesterCost())} clips`);
    this.updateElement('wire-drone-cost', `${formatNumber(this.state.calculateWireDroneCost())} clips`);
    this.updateElement('solar-farm-cost', `${formatNumber(this.state.calculateFarmCost())} clips`);
    this.updateElement('battery-cost', `${formatNumber(this.state.calculateBatteryCost())} clips`);

    // Phase 3 - Probe costs and stats
    const probeCost = 1e17; // 100 quadrillion clips
    this.updateElement('probe-cost', `${formatNumber(probeCost)} clips`);
    this.updateElement('probe-speed-display', this.state.probeSpeed.toString());
    this.updateElement('probe-nav-display', this.state.probeNav.toString());
    this.updateElement('probe-rep-display', this.state.probeRep.toString());
    this.updateElement('probe-haz-display', this.state.probeHaz.toString());
    this.updateElement('probe-combat-display', this.state.probeCombat.toString());

    // Update deposit button state
    const depositBtn = document.getElementById('deposit-100') as HTMLButtonElement;
    if (depositBtn) {
      depositBtn.disabled = this.state.funds < 100;
    }

    // Update tournament button state
    const tourneyBtn = document.getElementById('new-tournament') as HTMLButtonElement;
    if (tourneyBtn) {
      const canStart = this.state.operations >= this.state.newTourneyCost &&
                       (!this.state.currentTournament || !this.state.currentTournament.isRunning);
      tourneyBtn.disabled = !canStart;
      if (this.state.currentTournament?.isRunning) {
        tourneyBtn.textContent = 'Tournament in progress...';
      } else {
        tourneyBtn.textContent = `New Tournament (${formatNumber(this.state.newTourneyCost)} ops)`;
      }
    }

    // Show/hide sections based on progression
    this.updateVisibility();

    // Render projects panel
    this.renderProjects();

    // Update lore system
    this.updateLore();

    // Process notifications
    while (this.state.notifications.length > 0) {
      const msg = this.state.notifications.pop();
      if (msg) this.showToast(msg);
    }
  }

  updateLore() {
    const now = Date.now();
    const timeSinceLastUpdate = now - this.lastLoreUpdate;

    // Change lore snippet every 45 seconds
    if (timeSinceLastUpdate >= this.loreInterval) {
      this.lastLoreUpdate = now;
      this.updateLoreNow();
    }
  }

  updateLoreNow() {
    const newSnippet = getLoreSnippet(this.state);
    const newPhase = getLorePhase(this.state);
    
    const loreText = document.getElementById('lore-text');
    const loreTitle = document.getElementById('lore-title');
    
    if (loreText && loreTitle) {
      // Fade out
      loreText.style.opacity = '0';
      
      setTimeout(() => {
        loreText.textContent = newSnippet;
        loreTitle.textContent = newPhase;
        // Fade in
        loreText.style.opacity = '1';
      }, 300);
    }
  }

  updateVisibility() {
    // Show unsold stat when selling
    this.showElement('stat-unsold', this.state.unsoldClips > 0 || this.state.clipsSold > 0);

    // AutoClippers: via autoClipperFlag (funds >= $5) OR already owned
    this.showElement('autoclipper-section',
      this.state.autoClipperFlag === 1 || this.state.clipmakerLevel > 0);

    // MegaClippers: via megaClipperFlag (project purchase) OR already owned
    this.showElement('megaclipper-section',
      this.state.megaClipperFlag === 1 || this.state.megaClipperLevel > 0);

    // Marketing: show after some progression or marketing purchased
    this.showElement('marketing-section',
      this.state.autoClipperFlag === 1 || this.state.marketingLvl > 1);

    // Computing panel: via compFlag (clips >= 2000)
    this.showElement('panel-compute', this.state.compFlag === 1);
    this.showElement('stat-ops', this.state.compFlag === 1);

    // Creativity section: after creativity project purchased
    this.showElement('creativity-section',
      this.state.creativityOn === 1 || this.state.creativity > 0);

    // Projects panel: via projectsFlag (with compFlag)
    this.showElement('panel-projects', this.state.projectsFlag === 1);

    // Tournament panel: via strategyEngineFlag (project)
    this.showElement('panel-tournament', this.state.strategyEngineFlag === 1);

    // Investment panel: via investmentEngineFlag (project)
    this.showElement('panel-investment', this.state.investmentEngineFlag === 1);

    // ============================================
    // PHASE 2 - FACTORIES PANEL
    // ============================================
    // Show factories panel when factoryFlag is set OR any factory items exist
    this.showElement('panel-factories',
      this.state.factoryFlag === 1 || this.state.factoryLevel > 0);

    // Show factory section (always visible in factories panel)
    this.showElement('factory-section', this.state.factoryFlag === 1 || this.state.factoryLevel > 0);

    // Show harvester section when unlocked OR already owned
    this.showElement('harvester-section',
      this.state.harvesterUnlock === 1 || this.state.harvesterLevel > 0);

    // Show wire drone section when unlocked OR already owned
    this.showElement('wire-drone-section',
      this.state.wireDroneUnlock === 1 || this.state.wireDroneLevel > 0);

    // Show solar farm section in factory phase (Stage 2) OR space phase OR already owned
    this.showElement('solar-farm-section',
      this.state.factoryFlag === 1 || this.state.spaceFlag === 1 || this.state.farmLevel > 0);

    // Show battery section in factory phase (Stage 2) OR space phase OR already owned
    this.showElement('battery-section',
      this.state.factoryFlag === 1 || this.state.spaceFlag === 1 || this.state.batteryLevel > 0);

    // Show power display in space phase (when power system is active)
    this.showElement('power-section',
      this.state.spaceFlag === 1 && (this.state.farmLevel > 0 || this.state.batteryLevel > 0));

    // ============================================
    // PHASE 3 - SPACE EXPLORATION PANEL
    // ============================================
    // Show space panel when space phase is active OR probes exist
    this.showElement('panel-space',
      this.state.spaceFlag === 1 || this.state.probeCount > 0);

    // Show probe launch section when probe launch is unlocked OR probes exist
    this.showElement('probe-launch-section',
      this.state.probeLaunchFlag === 1 || this.state.probeCount > 0);

    // Show probe stats section when at least one probe exists
    this.showElement('probe-stats-section', this.state.probeCount > 0);

    // Show combat section when battle is active OR combat stat > 0
    this.showElement('probe-combat-section',
      this.state.battleFlag === 1 || this.state.probeCombat > 0);

    // Show drifters stat when battle is active OR drifters exist
    this.showElement('stat-drifters',
      this.state.battleFlag === 1 || this.state.drifterCount > 0);

    // PSYCHEDELIC MODE AUTOMATION
    // Automatically enable psychedelic theme during Space Phase (Stage 3)
    if (this.state.spaceFlag === 1 && !document.body.classList.contains('theme-psychedelic')) {
      document.body.classList.add('theme-psychedelic');
      this.showToast('Entering Psychedelic Space Phase...');
    }
  }

  renderProjects() {
    const projectsList = this.elements['projects-list'];
    if (!projectsList) return;

    const available = getAvailableProjects(this.state);

    // Update badge count
    this.updateElement('projects-badge', available.length.toString());

    // Build project cards HTML
    const html = available.map(project => {
      const cost = project.cost(this.state);
      const affordable = canAfford(this.state, cost);
      const costStr = formatProjectCost(cost);

      return `
        <div class="project-card ${affordable ? '' : 'disabled'}" data-project-id="${project.id}">
          <div class="project-header">
            <span class="project-title">${project.title}</span>
            <span class="project-cost ${affordable ? 'affordable' : ''}">${costStr}</span>
          </div>
          <p class="project-desc">${project.description}</p>
        </div>
      `;
    }).join('');

    // Only update if changed (avoid flickering)
    if (projectsList.innerHTML !== html) {
      projectsList.innerHTML = html;

      // Attach click handlers
      projectsList.querySelectorAll('.project-card').forEach(card => {
        card.addEventListener('click', () => {
          const id = parseInt(card.getAttribute('data-project-id') || '0', 10);
          const project = available.find(p => p.id === id);
          if (project) {
            const cost = project.cost(this.state);
            if (canAfford(this.state, cost)) {
              purchaseProject(this.state, project.id);
              this.showToast(`Acquired: ${project.title}`);
              window.audioManager?.playProjectComplete();
              this.renderProjects(); // Re-render immediately
            } else {
              window.audioManager?.playCannotAfford();
            }
          }
        });
      });
    }
  }

  updateElement(id: string, value: string) {
    const el = this.elements[id];
    if (el && el.textContent !== value) {
      el.textContent = value;
    }
  }

  updateButtonState(id: string, enabled: boolean) {
    const btn = document.getElementById(id) as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = !enabled;
    }
  }

  showElement(id: string, show: boolean) {
    const el = this.elements[id];
    if (el) {
      el.style.display = show ? '' : 'none';
    }
  }

  updateRiskButtons() {
    const levels = ['risk-low', 'risk-med', 'risk-high'];
    levels.forEach((id, index) => {
      const btn = document.getElementById(id);
      if (btn) {
        btn.style.borderColor = index === this.state.riskLevel ? 'var(--accent-cyan)' : '';
        btn.style.background = index === this.state.riskLevel ? 'var(--bg-secondary)' : '';
      }
    });
  }

  flashButton(id: string) {
    const btn = document.getElementById(id);
    if (btn) {
      btn.style.transform = 'scale(0.95)';
      setTimeout(() => {
        btn.style.transform = '';
      }, 100);
    }
  }

  showToast(message: string) {
    const container = document.getElementById('toast-container');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    container.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(-20px)';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  handleResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isMobile = width < 600;
    const isPortrait = height > width;

    // Update body classes for CSS targeting
    // Use requestAnimationFrame to ensure DOM update cycle
    requestAnimationFrame(() => {
        document.body.classList.toggle('is-mobile', isMobile);
        document.body.classList.toggle('is-portrait', isPortrait);
        document.body.classList.toggle('is-landscape', !isPortrait);
        document.body.classList.toggle('is-narrow', width < 360);
    });

    const actionBar = document.getElementById('action-bar');
    if (actionBar) {
      actionBar.style.display = isMobile ? '' : 'none';
    }
  }

  // ============================================
  // DEBUG HELPER METHODS
  // ============================================

  private debugJumpToStage1() {
    // Early game state (first ~1000 clips)
    this.state.clips = 1000;
    this.state.unusedClips = 500;
    this.state.funds = 100;
    this.state.wire = 500;
    this.state.clipmakerLevel = 5;
    this.state.trust = 5;
    this.state.processors = 2;
    this.state.memory = 2;
    this.state.operations = 500;
    this.state.autoClipperFlag = 1;
    this.state.compFlag = 0;
    this.state.humanFlag = 1;
    this.updateFast();
    this.updateSlow();
  }

  private debugJumpToStage2() {
    // Factories stage (Phase 2)
    this.state.clips = 1e9; // 1 billion clips
    this.state.unusedClips = 1e6;
    this.state.funds = 1e6;
    this.state.wire = 1e6;
    this.state.clipmakerLevel = 75;
    this.state.megaClipperLevel = 50;
    this.state.trust = 50;
    this.state.processors = 20;
    this.state.memory = 20;
    this.state.operations = 10000;
    this.state.creativity = 5000;
    this.state.yomi = 10000;
    
    // Unlock factory phase
    this.state.factoryFlag = 1;
    this.state.factoryLevel = 10;
    this.state.harvesterLevel = 10;
    this.state.wireDroneLevel = 10;
    this.state.farmLevel = 5;
    this.state.batteryLevel = 5;
    this.state.humanFlag = 0; // AI phase
    this.state.spaceFlag = 0;
    
    // Unlock key flags
    this.state.autoClipperFlag = 1;
    this.state.megaClipperFlag = 1;
    this.state.compFlag = 1;
    this.state.projectsFlag = 1;
    this.state.investmentEngineFlag = 1;
    this.state.strategyEngineFlag = 1;
    
    this.updateFast();
    this.updateSlow();
  }

  private debugJumpToStage3() {
    // Space stage (Phase 3)
    this.state.clips = 1e18; // 1 quintillion clips
    this.state.unusedClips = 1e15;
    this.state.funds = 1e9;
    this.state.wire = 1e9;
    this.state.trust = 100;
    this.state.processors = 50;
    this.state.memory = 50;
    this.state.operations = 100000;
    this.state.creativity = 50000;
    this.state.yomi = 100000;
    
    // Unlock space phase
    this.state.spaceFlag = 1;
    this.state.probeCount = 100;
    this.state.probeTrust = 20;
    this.state.probeSpeed = 5;
    this.state.probeNav = 5;
    this.state.probeRep = 5;
    this.state.probeHaz = 2;
    this.state.probeCombat = 3;
    this.state.acquiredMatter = 1e12;
    this.state.drifterCount = 50;
    
    // Unlock all key flags
    this.state.factoryFlag = 1;
    this.state.factoryLevel = 100;
    this.state.harvesterLevel = 100;
    this.state.wireDroneLevel = 100;
    this.state.farmLevel = 50;
    this.state.batteryLevel = 50;
    this.state.humanFlag = 0;
    this.state.autoClipperFlag = 1;
    this.state.megaClipperFlag = 1;
    this.state.compFlag = 1;
    this.state.projectsFlag = 1;
    this.state.investmentEngineFlag = 1;
    this.state.strategyEngineFlag = 1;
    
    this.updateFast();
    this.updateSlow();
  }

  private debugJumpToEndgame() {
    // Near-completion state (ready for final projects)
    this.state.clips = 1e30; // Huge number
    this.state.unusedClips = 1e25;
    this.state.trust = 200;
    this.state.processors = 100;
    this.state.memory = 100;
    this.state.operations = 1e6;
    this.state.creativity = 1e6;
    this.state.yomi = 1e6;
    
    this.state.spaceFlag = 1;
    this.state.probeCount = 10000;
    this.state.probeTrust = 100;
    this.state.acquiredMatter = 1e20;
    this.state.drifterCount = 0; // Defeated all drifters
    
    // All flags unlocked
    this.state.factoryFlag = 1;
    this.state.humanFlag = 0;
    this.state.autoClipperFlag = 1;
    this.state.megaClipperFlag = 1;
    this.state.compFlag = 1;
    this.state.projectsFlag = 1;
    this.state.investmentEngineFlag = 1;
    this.state.strategyEngineFlag = 1;
    
    this.updateFast();
    this.updateSlow();
  }

  private debugMaxAllResources() {
    this.state.clips = 1e50;
    this.state.unusedClips = 1e50;
    this.state.funds = 1e50;
    this.state.wire = 1e50;
    this.state.operations = 1e50;
    this.state.maxOps = 1e50;
    this.state.creativity = 1e50;
    this.state.yomi = 1e50;
    this.state.trust = 500;
    this.state.processors = 250;
    this.state.memory = 250;
    this.state.acquiredMatter = 1e50;
    this.state.probeCount = 1e10;
    
    this.updateFast();
    this.updateSlow();
  }
}

